import { useState, useEffect } from 'react';
import { 
  Save, 
  X, 
  AlertCircle, 
  CheckCircle, 
  Loader2
} from 'lucide-react';
import { ProjectData } from '../types';

interface AdminFieldEditorProps {
  project: ProjectData;
  isOpen: boolean;
  onClose: () => void;
  onSave: (fields: Record<string, string>) => Promise<void>;
}

interface AdminFields {
  firebaseProjectId: string;
  olafGroupId: string;
  olafToken: string;
  olafApiKey: string;
  appleKeyId: string;
  appleIssuerId: string;
}

export function AdminFieldEditor({ project, isOpen, onClose, onSave }: AdminFieldEditorProps) {
  const [fields, setFields] = useState<AdminFields>({
    firebaseProjectId: '',
    olafGroupId: '',
    olafToken: '',
    olafApiKey: '',
    appleKeyId: '',
    appleIssuerId: '',
  });

  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Re-hydrate whenever the dialog opens or project formData changes after a sync
  useEffect(() => {
    if (!isOpen) return;
    setFields({
      firebaseProjectId: project.formData?.firebaseProjectId || '',
      olafGroupId: project.formData?.olafGroupId || '',
      olafToken: project.formData?.olafToken || '',
      olafApiKey: project.formData?.olafApiKey || '',
      appleKeyId: project.formData?.appleKeyId || '',
      appleIssuerId: project.formData?.appleIssuerId || '',
    });
    setSaveStatus('idle');
    setErrorMessage('');
  }, [isOpen, project.formData, project.jiraKey]);

  const handleFieldChange = (fieldName: keyof AdminFields, value: string) => {
    setFields(prev => ({
      ...prev,
      [fieldName]: value
    }));
    setSaveStatus('idle');
    setErrorMessage('');
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveStatus('idle');
      setErrorMessage('');

      // Filter out empty fields
      const fieldsToSave = Object.entries(fields).reduce((acc, [key, value]) => {
        if (value.trim()) {
          acc[key] = value.trim();
        }
        return acc;
      }, {} as Record<string, string>);

      await onSave(fieldsToSave);
      setSaveStatus('success');
      
      // Auto-close after success
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error saving admin fields:', error);
      setSaveStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to save fields');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Edit Admin Fields
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {project.jiraKey} - {project.customerName}
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={isSaving}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Status Messages */}
          {saveStatus === 'success' && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center">
              <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
              <span className="text-green-700 text-sm">Fields saved successfully!</span>
            </div>
          )}

          {saveStatus === 'error' && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-700 text-sm">{errorMessage}</span>
            </div>
          )}

          {/* Form Fields */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Firebase Project ID
              </label>
              <input
                type="text"
                value={fields.firebaseProjectId}
                onChange={(e) => handleFieldChange('firebaseProjectId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., ima-onboard"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                OLAF Group ID
              </label>
              <input
                type="text"
                value={fields.olafGroupId}
                onChange={(e) => handleFieldChange('olafGroupId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., group-123"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                OLAF Token
              </label>
              <input
                type="text"
                value={fields.olafToken}
                onChange={(e) => handleFieldChange('olafToken', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="OLAF authentication token"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                OLAF API Key
              </label>
              <input
                type="text"
                value={fields.olafApiKey}
                onChange={(e) => handleFieldChange('olafApiKey', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="OLAF API key"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Apple Key ID
              </label>
              <input
                type="text"
                value={fields.appleKeyId}
                onChange={(e) => handleFieldChange('appleKeyId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., ABC123DEF4"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Apple Issuer ID
              </label>
              <input
                type="text"
                value={fields.appleIssuerId}
                onChange={(e) => handleFieldChange('appleIssuerId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 12345678-1234-1234-1234-123456789012"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
            <button
              onClick={handleClose}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Fields
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
